<template>
  <div class="flex w-full items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
    
    <!-- LEFT SIDE: Toggle (Mobile Only) + Page Title -->
    <div class="flex items-center gap-x-4">
      <!-- Toggle Button: Hidden on desktop (md:hidden) -->
      <button 
        @click="$emit('toggle-sidebar')"
        class="md:hidden p-2 -ml-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Toggle sidebar"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      
      <h1 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
        {{ pageTitle }}
      </h1>
    </div>
    
    <!-- RIGHT SIDE: Actions -->
    <div class="flex items-center gap-x-2 sm:gap-x-4">
      
      <!-- Dark Mode Toggle -->
      <button 
        @click="$emit('toggle-dark-mode')"
        class="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Toggle dark mode"
      >
        <svg v-if="!isDarkMode" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      </button>
      
      <!-- Notifications -->
        <!-- ✅ NOTIFICATIONS DROPDOWN (Employee View) -->
      <div ref="notificationDropdownRef" class="relative">
        <button 
          @click="isNotificationOpen = !isNotificationOpen" 
          class="relative p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <!-- Badge -->
          <span v-if="unreadCount > 0" class="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-800">
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </button>
        
        <!-- Dropdown Menu -->
        <div v-if="isNotificationOpen" class="absolute right-0 z-50 mt-2 w-80 origin-top-right rounded-xl bg-white dark:bg-gray-800 py-2 shadow-lg ring-1 ring-gray-900/5 dark:ring-gray-700">
          <div class="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
            <button v-if="unreadCount > 0" @click="markAllAsRead" class="text-xs text-[#035a76] dark:text-blue-400 hover:underline font-medium">
              Mark all read
            </button>
          </div>
          
          <div class="max-h-96 overflow-y-auto">
            <div v-if="myNotifications.length === 0" class="px-4 py-8 text-center">
              <p class="text-sm text-gray-500 dark:text-gray-400">No new notifications</p>
            </div>
            
            <div v-else>
              <div 
                v-for="note in myNotifications" 
                :key="note.id"
                @click="markAsRead(note.id)"
                class="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-50 dark:border-gray-700 last:border-0 cursor-pointer"
                :class="{ 'bg-blue-50/50 dark:bg-blue-900/10': !isRead(note.id) }"
              >
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0">
                    <div class="h-8 w-8 rounded-full flex items-center justify-center" 
                         :class="note.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-red-100 dark:bg-red-900/30 text-red-600'">
                      <svg v-if="note.status === 'approved'" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <svg v-else class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      Leave Request {{ note.status === 'approved' ? 'Approved' : 'Rejected' }}
                    </p>
                    <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                      Your <span class="font-semibold">{{ note.type }}</span> leave was {{ note.status }}.
                    </p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {{ formatDate(note.updatedAt) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Divider -->
      
      <!-- User Dropdown (Your existing code goes here) -->>
      
      <!-- Divider -->
      <div class="hidden sm:block h-6 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true"></div>
      
      <!-- User Dropdown -->
      <div ref="userDropdownRef" class="relative">
        <button 
          @click="toggleUserDropdown"
          class="flex items-center gap-x-3 rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none"
          aria-expanded="isUserDropdownOpen"
        >
          <div class="h-8 w-8 rounded-full bg-[#035a76] flex items-center justify-center text-sm font-semibold text-white shadow-sm">
            {{ getInitials(user?.name) }}
          </div>
          <div class="hidden md:flex flex-col items-start">
            <span class="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{{ user?.name || "Guest" }}</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">Academic Staff</span>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" class="hidden md:block h-4 w-4 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        <!-- Dropdown Menu -->
        <transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="transform opacity-0 scale-95"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0 scale-95"
        >
          <div 
            v-if="isUserDropdownOpen"
            class="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl bg-white dark:bg-gray-800 py-2 shadow-lg ring-1 ring-gray-900/5 dark:ring-gray-700 focus:outline-none"
          >
            <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700 md:hidden">
              <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ user?.name || "Guest" }}</p>
            </div>
            <a href="#" class="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <svg class="w-4 h-4 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Your Profile
            </a>
            <a href="#" class="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <svg class="w-4 h-4 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </a>
            <div class="my-1 border-t border-gray-100 dark:border-gray-700"></div>
            <button @click.prevent="logout" class="flex w-full items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <svg class="w-4 h-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuth } from '@/composables/useAuth';
import api from '@/services/api'; // ✅ Make sure this is imported

const { locale } = useI18n();
const { logout } = useAuth();
const router = useRouter();

const props = defineProps({
  pageTitle: { type: String, default: 'Dashboard' },
  isDarkMode: { type: Boolean, default: false }
});

defineEmits(['toggle-sidebar', 'toggle-dark-mode', 'change-lang', 'logout']);

const user = ref(null);
const isUserDropdownOpen = ref(false);
const userDropdownRef = ref(null);

// ✅ NOTIFICATION STATE
const isNotificationOpen = ref(false);
const notificationDropdownRef = ref(null);
const myNotifications = ref([]);
const viewedNotificationIds = ref([]);
const UNREAD_STORAGE_KEY = 'employee_viewed_notifications';

// Get current user ID
const currentUserId = computed(() => {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) return null;
  try {
    const u = JSON.parse(storedUser);
    return u.id ? String(u.id) : null;
  } catch {
    return null;
  }
});

const loadViewedIds = () => {
  try {
    const stored = localStorage.getItem(UNREAD_STORAGE_KEY);
    viewedNotificationIds.value = stored ? JSON.parse(stored) : [];
  } catch {
    viewedNotificationIds.value = [];
  }
};

const saveViewedIds = () => {
  localStorage.setItem(UNREAD_STORAGE_KEY, JSON.stringify(viewedNotificationIds.value));
};

const unreadCount = computed(() => {
  return myNotifications.value.filter(n => !viewedNotificationIds.value.includes(String(n.id))).length;
});

const isRead = (id) => viewedNotificationIds.value.includes(String(id));

const markAsRead = (id) => {
  if (!viewedNotificationIds.value.includes(String(id))) {
    viewedNotificationIds.value.push(String(id));
    saveViewedIds();
  }
  isNotificationOpen.value = false; // Close dropdown on click
};

const markAllAsRead = () => {
  const allIds = myNotifications.value.map(n => String(n.id));
  viewedNotificationIds.value = [...new Set([...viewedNotificationIds.value, ...allIds])];
  saveViewedIds();
};

const fetchMyNotifications = async () => {
  if (!currentUserId.value) return;
  
  try {
    // ✅ Use EmployeeLeaves.php with user_id, just like the history page
    const response = await api.get(`EmployeeLeaves.php?user_id=${currentUserId.value}`);
    const allLeaves = response.data?.data || [];
    
    // Since EmployeeLeaves.php already filters for this specific user, 
    // we only need to filter by status (Approved or Rejected)
    const recentUpdates = allLeaves
      .filter(l => l.status === 'approved' || l.status === 'rejected')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5) // Show last 5
      .map(l => ({
        id: l.id,
        type: l.leave_type_name,
        status: l.status,
        updatedAt: l.created_at 
      }));
      
    myNotifications.value = recentUpdates;
    
    // Cleanup viewed IDs that are no longer in the list (e.g., if deleted)
    const currentIds = recentUpdates.map(n => String(n.id));
    viewedNotificationIds.value = viewedNotificationIds.value.filter(id => currentIds.includes(id));
    saveViewedIds();
    
  } catch (error) {
    console.error('Error fetching notifications:', error);
  }
};

const handleClickOutside = (event) => {
  if (userDropdownRef.value && !userDropdownRef.value.contains(event.target)) {
    isUserDropdownOpen.value = false;
  }
  if (notificationDropdownRef.value && !notificationDropdownRef.value.contains(event.target)) {
    isNotificationOpen.value = false;
  }
};

const getInitials = (name) => {
  if (!name) return "G";
  const parts = name.trim().split(" ");
  return parts.length === 1 ? parts[0].charAt(0).toUpperCase() : (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

onMounted(() => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) user.value = JSON.parse(storedUser);
  
  document.addEventListener('click', handleClickOutside);
  loadViewedIds();
  fetchMyNotifications();
  
  // Auto-refresh notifications every 60 seconds
  setInterval(fetchMyNotifications, 60000);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Smooth transitions for dark mode elements */
.bg-white, .bg-gray-50, .bg-gray-800, .bg-gray-900 {
  transition: background-color 0.3s ease;
}
.text-gray-900, .text-gray-500, .text-gray-400 {
  transition: color 0.3s ease;
}
.border-gray-100, .border-gray-200, .border-gray-700 {
  transition: border-color 0.3s ease;
}
</style>